"use client";

import { useState } from "react";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { deleteDocumentPermission, updateDocumentPermission } from "@/api";
import {
  NotificationAcceptButton,
  NotificationActions,
  NotificationDeclineButton,
  NotificationLinkButton,
} from "@/components/doc/left-sidebar/top-menu/notifications/NotificationActions";
import { NotificationRow } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationRow";
import { useStore } from "@/store";
import { docPageUrl, teamPageUrl } from "@/urls";
import { trackEvent } from "@/utils/trackEvent";

import type { DocumentPermission, Notification } from "@/api";

const ALLOW_KEYS = {
  write: "allowEditing",
  comment: "allowComment",
  read: "allowView",
} as const;

export function DocumentPermissionNotificationItem({
  notification,
  entity,
}: {
  notification: Notification;
  entity: DocumentPermission;
}) {
  const t = useTranslations("sidebar.notifications");
  const tShare = useTranslations("share");
  const tDocTitle = useTranslations("doc.documentTitle");
  const store = useStore();
  const docTitle = entity.document.title ?? tDocTitle("placeholder");
  const bodyKey = entity.confirmed ? "documentPermission.granted" : "documentPermission.requested";

  const [busy, setBusy] = useState<"accept" | "decline" | null>(null);
  const [resolved, setResolved] = useState(false);

  // TODO: handle errors https://linear.app/corca-research/issue/D-2963
  const onAccept = async () => {
    if (busy) return;
    trackEvent("notification_action_clicked", { type: "document_permission", action: "accept" });
    setBusy("accept");
    try {
      await updateDocumentPermission(entity.uid, { scope: entity.scope });
      setResolved(true);
    } finally {
      setBusy(null);
    }
  };

  const onDecline = async () => {
    if (busy) return;
    trackEvent("notification_action_clicked", { type: "document_permission", action: "decline" });
    setBusy("decline");
    try {
      await deleteDocumentPermission(entity.uid);
      setResolved(true);
    } finally {
      setBusy(null);
    }
  };

  const allowKey = ALLOW_KEYS[entity.scope as keyof typeof ALLOW_KEYS];

  const href = docPageUrl({
    docId: entity.document.uid,
    teamPermalink: entity.team?.permalink,
  });

  const skipIfCurrentDoc = () => store.doc?.id === entity.document.uid;

  return (
    <NotificationRow
      notification={notification}
      eventType="document_permission"
      badge={entity.confirmed ? "shared" : "request"}
      href={href}
      onNavigate={skipIfCurrentDoc}
    >
      <p className="text-[15px] leading-[18px] tracking-[-0.02em]">
        {t.rich(bodyKey, {
          senderName: notification.sender.name,
          name: docTitle,
          scope: entity.scope,
          user: (chunks) => (
            <Link
              href={teamPageUrl({ teamPermalink: notification.sender.username })}
              className="relative font-semibold hover:underline"
            >
              {chunks}
            </Link>
          ),
          document: (chunks) => (
            <Link
              href={docPageUrl({
                docId: entity.document.uid,
                teamPermalink: entity.team?.permalink,
              })}
              className="relative font-semibold hover:underline"
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
      {entity.confirmed && (
        <NotificationActions>
          <NotificationLinkButton
            href={docPageUrl({ docId: entity.document.uid, teamPermalink: entity.team?.permalink })}
            onClick={() =>
              trackEvent("notification_action_clicked", {
                type: "document_permission",
                action: "open_document",
              })
            }
          >
            {t("documentPermission.openDocument")}
          </NotificationLinkButton>
        </NotificationActions>
      )}
      {!entity.confirmed && !resolved && (
        <NotificationActions>
          <NotificationDeclineButton disabled={!!busy} onClick={onDecline}>
            {tShare("decline")}
          </NotificationDeclineButton>
          <NotificationAcceptButton isBusy={busy === "accept"} onClick={onAccept}>
            {tShare(allowKey)}
          </NotificationAcceptButton>
        </NotificationActions>
      )}
    </NotificationRow>
  );
}
